import { format } from 'date-fns'
import { cardOutline } from 'ionicons/icons'
import type { GooglePayPaymentMethodType, SupportedPaymentMethod } from '~/types/payment'

/**
 * @description Map of supported payment methods to their corresponding Ionicons icon name
 */
export const PaymentIconMap: Record<SupportedPaymentMethod | string, string> = {
  amex: 'amex',
  mc: 'mastercard',
  visa: 'visa',
  card: cardOutline,
  scheme: cardOutline, // Adyen's payment method type for credit card
  applepay: 'apple-pay',
  paywithgoogle: 'google-pay',
  googlepay: 'google-pay'
}

export function isGooglePayPaymentType(type?: string | null): type is GooglePayPaymentMethodType {
  return type === 'googlepay' || type === 'paywithgoogle'
}

/**
 * @description Get icon name for a given payment type
 * @param paymentType {SupportedPaymentMethod | string}
 * @returns {string} - icon name
 */
export function getPaymentIcon(paymentType?: SupportedPaymentMethod | string | null): string {
  if (!paymentType) return PaymentIconMap['scheme']

  return PaymentIconMap[paymentType] ?? cardOutline
}

export function getThreeDSActionHandler({
  isCapacitor
}: {
  isCapacitor: boolean
  paymentMethodType?: string | null
}): 'native' | 'web' {
  // The Adyen action handler follows the request channel. Every Capacitor payment uses
  // the native SDK, regardless of whether the initiating method was scheme or a wallet.
  return isCapacitor ? 'native' : 'web'
}

/**
 * @description Format card title for a given card used in payment methods list
 * @param lastFourDigits {string}
 * @returns {string} - Formatted card title e.g. '••••1234'
 */
export function formatCardTitle(lastFourDigits?: string): string {
  if (!lastFourDigits) return 'Credit / Debit Card'

  return '••••' + lastFourDigits
}

/**
 * @description Converts cents to dollars and formats the price in AUD
 * @param cents {number} - The price in cents
 * @returns {string} - The price in dollars
 */
export function formatPrice(cents: number): string {
  const formatter = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD'
  })
  return formatter.format(cents / 100)
}

/**
 * @description Converts cents to dollars and formats the price in AUD, removing trailing zeroes
 * @param cents {number} - The price in cents
 * @returns {string} - The price in dollars
 */
export function formatPriceNoTrailingZeroes(cents: number): string {
  // browsers have an option for this that doesn't seem to be available to us here :(
  //   trailingZeroDisplay: 'stripIfInteger'
  const formatter = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD'
  })
  return formatter.format(cents / 100).replace(/\.00$/, '')
}

/**
 * @description Checks the card expiry date to see if it is expired
 * @param expiryYear {string} - The expiry year of the card in 2 or 4 digit format
 * @param expiryMonth {string} - The expiry month of the card in 2 digit format
 * @returns {boolean} - True if the card is expired
 */
export function getIsExpired(expiryYear: string | null | undefined, expiryMonth: string | null | undefined): boolean {
  if (!expiryYear || !expiryMonth) return false

  if (expiryYear.length === 4) {
    expiryYear = expiryYear.substring(2, 4)
  }

  // Expired if the year and month of the expiry date is before the year and month of the current date
  const now = new Date()
  const nowYear = format(now, 'yy')
  const nowMonth = now.getMonth() + 1

  return (
    parseInt(expiryYear) < parseInt(nowYear) ||
    (parseInt(expiryYear) === parseInt(nowYear) && parseInt(expiryMonth) < nowMonth)
  )
}
